import { useCallback, useRef, useState, type ReactNode } from 'react';
import ConfirmModal from './ConfirmModal';
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from './confirm-context';
import { useTranslation } from '../../hooks/useTranslation';

// Monta un único ConfirmModal para toda la app y expone confirm() por contexto
// (ver useConfirm). Se monta una vez, en la raíz de la app.
const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    // A second request while one is open cancels the first.
    resolveRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending(options);
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={pending?.title ?? ''}
        confirmLabel={
          pending?.confirmLabel ??
          (pending?.isDestructive ? t('Eliminar') : t('Confirmar'))
        }
        cancelLabel={pending?.cancelLabel}
        isDestructive={pending?.isDestructive}
      >
        {pending?.message ? (
          typeof pending.message === 'string' ? (
            <p className='text-sm text-default-500'>{pending.message}</p>
          ) : (
            pending.message
          )
        ) : null}
      </ConfirmModal>
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
